/**
 * The hosted app's side of the Plex-shaped cookies (shared/authCookie):
 * api.rago.tv sets rago_tv_auth (profile + session token) and
 * rago_tv_client_id (device id) on the parent domain; this module only READS
 * them — the SPA never writes a cookie in the normal course of things (a
 * script-written cookie is what Safari caps at seven days). Two exceptions,
 * both for robustness: clearing on sign-out while the broker is unreachable,
 * and re-planting the cookie right after a sign-in whose Set-Cookie did not
 * land (a misconfigured dev proxy, an over-eager extension).
 */
import {
  AUTH_COOKIE,
  AUTH_COOKIE_MAX_AGE_S,
  CLIENT_ID_COOKIE,
  parseAuthCookie,
  serializeAuthCookie,
  type AuthCookie,
  type User,
} from "@rago/web-shared";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function readCookie(name: string, jar: string = typeof document === "undefined" ? "" : document.cookie): string | null {
  for (const part of jar.split(";")) {
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    if (part.slice(0, eq).trim() === name) return part.slice(eq + 1).trim();
  }
  return null;
}

export function readAuthCookie(): AuthCookie | null {
  return parseAuthCookie(readCookie(AUTH_COOKIE));
}

/** The device id the API last saw for this browser, if the cookie survived. */
export function readClientIdCookie(): string | null {
  const v = readCookie(CLIENT_ID_COOKIE);
  return v && UUID_RE.test(v) ? v : null;
}

/** The Domain attribute the API used: the registrable parent ("rago.tv" for
 * app.rago.tv), none for localhost or a bare IP — so a local write or clear
 * addresses the same cookie the server set. */
export function cookieDomainFor(hostname: string): string | null {
  const h = hostname.toLowerCase();
  if (!h || h === "localhost" || h.includes(":") || /^[\d.]+$/.test(h)) return null;
  const labels = h.split(".").filter(Boolean);
  return labels.length >= 2 ? labels.slice(-2).join(".") : null;
}

function attrs(): string {
  const domain = cookieDomainFor(window.location.hostname);
  const secure = window.location.protocol === "https:" ? "; secure" : "";
  return `path=/${domain ? `; domain=${domain}` : ""}${secure}; samesite=lax`;
}

export function clearAuthCookieLocally(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${AUTH_COOKIE}=; ${attrs()}; max-age=0`;
}

/** After a sign-in: the server's Set-Cookie normally did this already. */
export function ensureAuthCookie(user: User, sessionToken: string): void {
  if (typeof document === "undefined") return;
  if (readAuthCookie()?.sessionToken === sessionToken) return;
  document.cookie = `${AUTH_COOKIE}=${serializeAuthCookie(user, sessionToken)}; ${attrs()}; max-age=${AUTH_COOKIE_MAX_AGE_S}`;
}
